import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Users, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, getStudentId } from "@/lib/api";
import { createStudySession, setActiveSessionCode } from "@/lib/studyApi";
import Navbar from "@/components/Navbar";

const DIFFICULTIES = ["beginner", "standard", "advanced", "adaptive"];
const MODES = [
  { value: "learn_practice", label: "Learn & Practice" },
  { value: "concept_questions", label: "Concept + Questions" },
  { value: "quiz_challenge", label: "Quiz Challenge" },
  { value: "exam_prep", label: "Exam Preparation" },
  { value: "revision", label: "Revision" },
];

export default function CreateStudySession() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [tree, setTree] = useState([]);
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [topicQuery, setTopicQuery] = useState("");
  const [difficulty, setDifficulty] = useState("adaptive");
  const [mode, setMode] = useState("learn_practice");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const studentId = getStudentId();
    api.get(`/students/${studentId}`).then(({ data }) => {
      setStudent(data);
      return api.get("/curriculum/tree", { params: { board: data.board, standard: data.standard } });
    }).then(({ data }) => {
      setTree(data);
      if (data.length) setSubject(data[0].name);
    }).catch(() => toast.error("Couldn't load curriculum."));
  }, []);

  const topicOptions = (tree.find((s) => s.name === subject)?.chapters || [])
    .flatMap((c) => c.concepts.map((cc) => cc.name));
  const filteredTopics = topicQuery
    ? topicOptions.filter((t) => t.toLowerCase().includes(topicQuery.toLowerCase()))
    : topicOptions;

  const create = async () => {
    const finalTopic = topic || topicQuery;
    if (!finalTopic.trim()) {
      toast.error("Please choose a topic to teach.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await createStudySession({
        host_student_id: student.id, subject, standard: student.standard, board: student.board,
        topic: finalTopic, difficulty, learning_mode: mode,
      });
      setActiveSessionCode(data.session_code);
      navigate(`/study/lobby/${data.session_code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the session.");
    } finally {
      setCreating(false);
    }
  };

  if (!student) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="create-study-session-page">
      <Navbar student={student} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-2 mb-1">
          <Users className="text-primary" size={22} />
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Select Your Learning Session</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">Class {student.standard} · {student.board}</p>

        <div className="space-y-7">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Subject</p>
            <div className="flex flex-wrap gap-2" data-testid="create-session-subjects">
              {tree.map((s) => (
                <button key={s.name} onClick={() => { setSubject(s.name); setTopic(""); setTopicQuery(""); }} data-testid={`subject-option-${s.name}`}
                  className={`px-4 py-2.5 rounded-full text-sm font-bold border-2 transition-colors ${subject === s.name ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}>
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Topic</p>
            <input
              data-testid="create-session-topic-input"
              value={topic || topicQuery}
              onChange={(e) => { setTopicQuery(e.target.value); setTopic(""); }}
              placeholder="Search or pick a topic..."
              className="w-full h-12 rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            {topicQuery && !topic && filteredTopics.length > 0 && (
              <div className="mt-2 border border-border rounded-xl divide-y divide-border max-h-48 overflow-y-auto" data-testid="topic-suggestions">
                {filteredTopics.slice(0, 8).map((t) => (
                  <button key={t} onClick={() => { setTopic(t); setTopicQuery(""); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted">
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Difficulty</p>
            <div className="flex flex-wrap gap-2" data-testid="create-session-difficulty">
              {DIFFICULTIES.map((d) => (
                <button key={d} onClick={() => setDifficulty(d)} data-testid={`difficulty-option-${d}`}
                  className={`px-4 py-2.5 rounded-full text-sm font-bold capitalize border-2 transition-colors ${difficulty === d ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary/40"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Learning Mode</p>
            <div className="flex flex-wrap gap-2" data-testid="create-session-mode">
              {MODES.map((m) => (
                <button key={m.value} onClick={() => setMode(m.value)} data-testid={`mode-option-${m.value}`}
                  className={`px-4 py-2.5 rounded-full text-sm font-bold border-2 transition-colors ${mode === m.value ? "bg-accent text-white border-accent" : "border-border hover:border-accent/40"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={create} disabled={creating} data-testid="create-session-submit-button"
            className="w-full h-13 py-3.5 rounded-full bg-primary text-primary-foreground font-bold text-base flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60">
            <Zap size={17} /> {creating ? "Creating Session..." : "Create Session"}
          </button>
          <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5">
            <Sparkles size={12} /> Up to 3 students can join with the Session ID you'll get next.
          </p>
        </div>
      </div>
    </div>
  );
}
